/**
 * Cross-tab auth: localStorage `token` is shared across same-origin windows,
 * but Redux is per-tab. A login in another window can leave this tab showing
 * the previous account while API calls use the new JWT.
 *
 * Listen for `storage` events on `token` (fires in other tabs only) and reload
 * so UI and API identity match. Do not call logout() — that would clear the
 * shared token and sign out the window that just logged in.
 */

export type CrossTabAuthChange = {
  hasSession: boolean
  key: string | null
  oldValue: string | null
  newValue: string | null
}

export function shouldReloadForCrossTabAuthChange({
  hasSession,
  key,
  oldValue,
  newValue,
}: CrossTabAuthChange): boolean {
  if (!hasSession) {
    return false
  }
  if (key === null) {
    return true
  }
  if (key !== 'token') {
    return false
  }
  return oldValue !== newValue
}

export type SubscribeToCrossTabAuthChangesOptions = {
  hasSession: () => boolean
  reload?: () => void
}

export function subscribeToCrossTabAuthChanges({
  hasSession,
  reload = () => window.location.reload(),
}: SubscribeToCrossTabAuthChangesOptions): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea && event.storageArea !== localStorage) {
      return
    }
    if (
      shouldReloadForCrossTabAuthChange({
        hasSession: hasSession(),
        key: event.key,
        oldValue: event.oldValue,
        newValue: event.newValue,
      })
    ) {
      reload()
    }
  }

  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}
